/**
 * Example usage of the Push Notification System
 *
 * This file demonstrates how to integrate push notifications into your React components
 */

import React, { useState, useEffect, useRef } from 'react';
import { PushNotificationManager } from '../utils/push';
import { useUserRole } from '../hooks/useUserRole';
import { useLanguage } from '../contexts/LanguageContext';

const PushNotificationExample = () => {
  const [pushManager, setPushManager] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [status, setStatus] = useState('Initializing...');
  const { canAccessAdmin } = useUserRole();
  const { t } = useLanguage();

  // Utiliser useRef pour éviter les réinitialisations
  const managerRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Éviter les doubles initialisations
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    // Check if push notifications are supported
    const supported = PushNotificationManager.isSupported();
    setIsSupported(supported);

    if (!supported) {
      setStatus(t('pushNotifications.notSupported'));
      return;
    }

    // Initialize push manager seulement une fois
    if (!managerRef.current) {
      const manager = new PushNotificationManager({
        onStateChange: (state, data) => {
          setStatus(`State: ${state.id}`);
          if (state.id === 'ERROR' || state.id === 'UNSUPPORTED') {
            console.error('Push notification error:', data);
          }
        },
        onSubscriptionChange: (sub) => {
          setSubscription(sub);
          setIsSubscribed(!!sub);
        },
        onError: (title, error) => {
          console.error(`${title}:`, error);
          setStatus(`Error: ${title}`);
        }
      });

      managerRef.current = manager;
      setPushManager(manager);
      setPermission(manager.getPermission());
    }

    // Update permission when it changes
    const checkPermission = () => {
      setPermission(Notification.permission);
    };

    // Listen for permission changes (not perfectly supported, but useful)
    document.addEventListener('visibilitychange', checkPermission);

    return () => {
      document.removeEventListener('visibilitychange', checkPermission);
    };
  }, []); // Dépendances vides pour n'exécuter qu'une fois

  const handleSubscribe = async () => {
    if (!pushManager) return;

    try {
      setStatus(t('pushNotifications.subscribing'));
      await pushManager.subscribe();
      setStatus(t('pushNotifications.subscribeSuccess'));
    } catch (error) {
      console.error('Subscription failed:', error);
      setStatus(`${t('pushNotifications.subscriptionFailed')}: ${error.message}`);
    }
  };

  const handleUnsubscribe = async () => {
    if (!pushManager) return;

    try {
      setStatus(t('pushNotifications.unsubscribing'));
      await pushManager.unsubscribe();
      setStatus(t('pushNotifications.unsubscribeSuccess'));
    } catch (error) {
      console.error('Unsubscription failed:', error);
      setStatus(`${t('pushNotifications.unsubscriptionFailed')}: ${error.message}`);
    }
  };

  const handleTestNotification = async () => {
    if (!pushManager || !isSubscribed) return;

    try {
      setStatus(t('pushNotifications.sendingTest'));

      const result = await pushManager.sendTestNotification(t('pushNotifications.testNotificationMessage'));

      if (result.success) {
        setStatus(`${t('pushNotifications.testSent')} (${result.sent_count}/${result.sent_count + result.failed_count} ${t('pushNotifications.resultSent')})`);
      } else {
        setStatus(`${t('pushNotifications.testFailed')}: ${result.message}`);
      }
    } catch (error) {
      console.error('Test notification failed:', error);
      setStatus(`${t('pushNotifications.testFailed')}: ${error.message}`);
    }
  };

  const handleBroadcastNotification = async () => {
    if (!pushManager || !isSubscribed) return;

    try {
      setStatus(t('pushNotifications.sendingBroadcast'));

      const response = await fetch('/api/push/admin/broadcast', {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed - this should be handled by your auth system
        },
        body: JSON.stringify({
          title: t('pushNotifications.broadcastTitle'),
          body: t('pushNotifications.broadcastMessage'),
          icon: '/sloot_logo.png',
          tag: 'broadcast-notification',
          data: {
            broadcast: true,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(t('pushNotifications.adminRequired'));
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setStatus(`${t('pushNotifications.broadcastSent')} (${result.sent_count}/${result.sent_count + result.failed_count} ${t('pushNotifications.resultSent')})`);
      } else {
        setStatus(`${t('pushNotifications.broadcastFailed')}: ${result.message}`);
      }
    } catch (error) {
      console.error('Broadcast notification failed:', error);
      setStatus(`${t('pushNotifications.broadcastFailed')}: ${error.message}`);
    }
  };

  if (!isSupported) {
    return (
      <div className="push-notifications">
        <h3>{t('pushNotifications.title')}</h3>
        <p>❌ {t('pushNotifications.notSupported')}</p>
      </div>
    );
  }

  return (
    <div className="push-notifications">
      <div className="info">
        <p><strong>{t('pushNotifications.permission')}:</strong> {permission}</p>
      </div>

      <div className="status">
        <strong>{t('pushNotifications.status')}:</strong> {status}
      </div>

      <div className="actions">
        {permission === 'default' && (
          <button onClick={handleSubscribe}>
            {t('pushNotifications.enableNotifications')}
          </button>
        )}

        {permission === 'granted' && !isSubscribed && (
          <button onClick={handleSubscribe}>
            {t('pushNotifications.subscribeToNotifications')}
          </button>
        )}

        {isSubscribed && (
          <>
            <button onClick={handleUnsubscribe}>
              {t('pushNotifications.unsubscribeFromNotifications')}
            </button>
            <button onClick={handleTestNotification}>
              {t('pushNotifications.sendTestNotification')}
            </button>
            {canAccessAdmin && (
              <button onClick={handleBroadcastNotification}>
                {t('pushNotifications.sendBroadcastNotification')}
              </button>
            )}
          </>
        )}

      </div>

      <div className="instructions">
        <h4>{t('pushNotifications.instructionsTitle')}</h4>
        <ul>
          <li>{t('pushNotifications.instruction1')}</li>
          <li>{t('pushNotifications.instruction2')}</li>
          <li>{t('pushNotifications.instruction3')}</li>
        </ul>
      </div>
    </div>
  );
};

export default PushNotificationExample;
